import { STATUS } from '@kontentino/kontentino-constants/Posts';
import { UserPermission } from 'constants/userPermission';
import { POST_STATUS_BY_USER_PERMISSION } from 'app/modules/posts/constants';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import { User } from 'types/User';
import type { TFunction } from 'i18next';
import { popupConfirm } from 'utils/popup';

export const SORTED_STATUSES = [
  STATUS.DRAFT,
  STATUS.COMPLETED,
  STATUS.WAITING_FOR,
  STATUS.DONE,
  STATUS.WAITING_FOR_INTERNAL_APPROVAL,
  STATUS.REJECTED,
  STATUS.INTERNAL_APPROVED,
  STATUS.AT_CLIENT,
  STATUS.REJECTED_BY_CLIENT,
  STATUS.APPROVED,
  STATUS.IS_SCHEDULING,
  STATUS.SCHEDULED,
  STATUS.POSTED,
  STATUS.EXPORTED,
  STATUS.EXPORT_FAILED,
] as number[];

export const createGetAvailableStatuses = (
  permissionsKeys: UserPermission[],
): ((pageTypes: number[]) => number[]) => {
  const statuses = getStatusesByUserPermissions(permissionsKeys);

  return (pageTypes: number[]) => {
    const availableStatuses = statuses.filter(
      (status) => status !== STATUS.IS_SCHEDULING,
    );

    if (!pageTypes.includes(TYPE.FACEBOOK)) {
      return availableStatuses.filter((status) => status !== STATUS.EXPORTED);
    }

    return availableStatuses;
  };
};

export const getStatusesByUserPermissions = (
  permissionsKeys: UserPermission[],
): number[] => {
  if (permissionsKeys.includes(UserPermission.POST_LIST_ALL)) {
    return Object.values(POST_STATUS_BY_USER_PERMISSION);
  }

  const statusesByPermissions = permissionsKeys
    .filter((permissionKey) => permissionKey in POST_STATUS_BY_USER_PERMISSION)
    .map((permissionKey) => POST_STATUS_BY_USER_PERMISSION[permissionKey]);

  return statusesByPermissions.sort((statusA, statusB) => {
    const indexA = SORTED_STATUSES.indexOf(statusA);
    const indexB = SORTED_STATUSES.indexOf(statusB);
    return indexA - indexB;
  });
};

/**
 * Statuses that put a post into (or report on) the publishing pipeline. Setting one of these
 * manually is a scheduling action, so it requires POST_SCHEDULE — the same permission that gates
 * every other scheduling control. A user without it (a client by default) may still *see* these
 * statuses through its post_list_* permissions, it just cannot set them.
 */
export const STATUSES_REQUIRING_SCHEDULE_PERMISSION = [
  STATUS.SCHEDULED,
  STATUS.POSTED,
  STATUS.EXPORTED,
  STATUS.EXPORT_FAILED,
  STATUS.FAILED,
] as number[];

export const canSetSchedulingStatuses = (
  permissionsKeys: UserPermission[],
): boolean => permissionsKeys.includes(UserPermission.POST_SCHEDULE);

/**
 * Whether `status` may be set manually by a user holding `permissionsKeys`.
 *
 * `availableStatuses` already narrows to what the user can see; this additionally drops the
 * scheduling statuses for users who cannot schedule, so the manual status dropdown and Time
 * Savers agree with the Schedule action itself.
 *
 * Kept free of any gate import so this module stays side-effect free for tests; callers go
 * through `filterManuallySettableStatuses`.
 */
export const canSetStatusManually = (
  status: number,
  permissionsKeys: UserPermission[] = [],
): boolean => {
  if (NOT_ALLOWED_EDIT_STATUSES.includes(status)) {
    return false;
  }

  return (
    !STATUSES_REQUIRING_SCHEDULE_PERMISSION.includes(status) ||
    canSetSchedulingStatuses(permissionsKeys)
  );
};

export const NOT_ALLOWED_EDIT_STATUSES = [STATUS.IS_SCHEDULING] as number[];

/**
 * [POST-WORKFLOW GATE — TEMPORARY] The pre-rebuild rule, kept verbatim so non-allowlisted
 * accounts behave exactly as they do today. Delete together with the gate after full cutover.
 */
const LEGACY_NOT_ALLOWED_STATUSES_CLIENT_ROLE = [
  STATUS.EXPORT_FAILED,
  STATUS.IS_SCHEDULING,
  STATUS.FAILED,
  STATUS.POSTED,
  STATUS.SCHEDULED,
] as number[];

const LEGACY_NOT_ALLOWED_EDIT_STATUSES = [
  STATUS.EXPORT_FAILED,
  STATUS.IS_SCHEDULING,
  STATUS.FAILED,
] as number[];

/** [POST-WORKFLOW GATE — TEMPORARY] */
export const canSetStatusManuallyLegacy = (
  role: User['role'],
  status: number,
): boolean =>
  role === 'client'
    ? !LEGACY_NOT_ALLOWED_STATUSES_CLIENT_ROLE.includes(status)
    : !LEGACY_NOT_ALLOWED_EDIT_STATUSES.includes(status);

export const NOT_DISPLAYED_KANBAN_STATUSES = [
  STATUS.EXPORT_FAILED,
  STATUS.IS_SCHEDULING,
] as number[];

export const ALREADY_EXPORTED_OR_PUBLISHED_STATUSES = [
  STATUS.POSTED,
  STATUS.EXPORTED,
] as number[];

export const confirmStatusChangeAwayFromPublished = async (
  currentStatus: number,
  nextStatus: number,
  t: TFunction,
  count = 1,
): Promise<boolean> => {
  const isChangingAway =
    ALREADY_EXPORTED_OR_PUBLISHED_STATUSES.includes(currentStatus) &&
    currentStatus !== nextStatus;

  if (!isChangingAway) {
    return true;
  }

  const warningKey =
    currentStatus === STATUS.EXPORTED
      ? 'changeExportedPostStatusWarning'
      : 'changePostedPostStatusWarning';

  const result = await popupConfirm({
    title: t('changeStatus', { defaultValue: 'Change status' }),
    text: t(warningKey, { count }),
    confirmButtonText: t('accept', { defaultValue: 'Accept' }),
    customClass: { popup: '!tw-w-[400px]' },
  });

  return result.isConfirmed;
};
